import usePositions from '@/application/read/positions/usePositions';
import ClaimRewardsReady from './states/Ready';
import useAddress from '@/application/read/web3/useAddress';
import { useMemo, useState } from 'react';
import useVaults from '@/application/read/vaults/useVaults';
import ClaimRewardsLoading from './states/Loading';
import ClaimRewardsSuccess from './states/Success';
import { useModalContext } from '@/presentation/context/modal';
import ClaimRewardsProcessing from './states/Processing';
import useLiquidityPools from '@/application/read/pools/useLiquidityPools';

const ClaimRewardsContainer = ({
  vaultId,
  viewMode = 'all',
}: {
  vaultId?: string;
  viewMode?: 'inventory' | 'liquidity' | 'all';
}) => {
  const { closeModal } = useModalContext();
  const userAddress = useAddress();
  const { data: [allIp, allLp] = [] } = usePositions({ userAddress, vaultId });
  const { data: vaults } = useVaults();
  const [state, setState] = useState<'view' | 'processing' | 'success'>('view');
  const { data: liquidityPools } = useLiquidityPools({ exists: true, vaultId });

  const inventoryPositions = useMemo(() => {
    if (viewMode === 'liquidity') {
      return [];
    }
    return allIp?.filter((p) => p.claimableRewards > 0n);
  }, [allIp, viewMode]);
  const liquidityPositions = useMemo(() => {
    if (viewMode === 'inventory') {
      return [];
    }
    return allLp?.filter((p) => p.claimableVToken > 0n || p.claimableEth > 0n);
  }, [allLp, viewMode]);

  const [lpClaims, setLpClaims] = useState(liquidityPositions || []);
  const [ipClaims, setIpClaims] = useState(inventoryPositions || []);

  if (
    !inventoryPositions ||
    !liquidityPositions ||
    !vaults ||
    !liquidityPools
  ) {
    return <ClaimRewardsLoading />;
  }

  switch (state) {
    case 'processing':
      return (
        <ClaimRewardsProcessing
          inventoryPositions={ipClaims}
          liquidityPositions={lpClaims}
          onComplete={() => setState('success')}
          onCancel={() => setState('view')}
          liquidityPools={liquidityPools}
          vaults={vaults}
        />
      );
    case 'success':
      return <ClaimRewardsSuccess onClose={closeModal} />;
    default:
      return (
        <ClaimRewardsReady
          inventoryPositions={inventoryPositions}
          liquidityPositions={liquidityPositions}
          onClaim={() => setState('processing')}
          setLpClaims={setLpClaims}
          lpClaims={lpClaims}
          ipClaims={ipClaims}
          setIpClaims={setIpClaims}
          vaults={vaults}
        />
      );
  }
};

export default ClaimRewardsContainer;
